import { Field } from '@base-ui/react/field';
import { cn } from 'components/utils';

import { InfoButton } from './InfoButton';
import { fieldLabelInfoRecipe, fieldLabelRowRecipe, fieldLabelTextRecipe } from './InfoButton.recipe';

import type { FieldInfoProps } from './types';

type FieldLabelProps = FieldInfoProps & {
  label: string;
  /** The field's own label recipe. Without `info` it goes on `Field.Label` exactly as before. */
  className: string;
  disabled?: boolean;
  /** Text Area's label also floats on `:placeholder-shown` — see `fieldLabelInfoRecipe`. */
  floatsOnPlaceholder?: boolean;
  /**
   * Draws an asterisk after the label text. Visual only — the control's native `required` is what
   * a screen reader announces, so the mark stays out of the accessible name.
   */
  required?: boolean;
  /**
   * The mark's colour recipe, from the field's own namespace (`textInputRequiredMarkRecipe`,
   * `datePickerRequiredMarkRecipe`, …), because a field may not read another field's slots.
   */
  requiredMarkClassName?: string;
};

const RequiredMark = ({ className }: { className?: string }) => (
  <span aria-hidden="true" className={className} data-part="required-mark">
    {' *'}
  </span>
);

/**
 * A field's label, with Figma's optional `Info` button after it.
 *
 * Shared by every labelled field so the five cannot place the button five ways. Without `info` it
 * renders the same `Field.Label` each field rendered before, so nothing changes for existing call
 * sites.
 *
 * With `info` the button sits BESIDE the label, never inside it. A button inside a `<label>` joins
 * the control's accessible name ("Email More information about Email"), so the label keeps only
 * its text and the row around it takes the positioning.
 *
 * The button is disabled with the field, as the trailing buttons are, and takes the label's
 * colour — including its disabled colour — through `currentColor`.
 */
export const FieldLabel = ({
  label,
  className,
  disabled,
  floatsOnPlaceholder,
  required,
  requiredMarkClassName,
  ...infoProps
}: FieldLabelProps) => {
  // Re-paired after destructuring, as `TextInput` does with its trailing trio.
  const info = infoProps.info && infoProps.infoLabel ? { text: infoProps.info, name: infoProps.infoLabel } : null;
  const mark = required ? <RequiredMark className={requiredMarkClassName} /> : null;

  if (!info) {
    return (
      <Field.Label className={className}>
        {label}
        {mark}
      </Field.Label>
    );
  }

  return (
    <span className={cn(className, fieldLabelRowRecipe())} data-part="label-row">
      <Field.Label className={fieldLabelTextRecipe()}>
        {label}
        {mark}
      </Field.Label>
      <span className={fieldLabelInfoRecipe({ floatsOnPlaceholder })} data-part="info">
        <InfoButton aria-label={info.name} disabled={disabled} label={info.text} />
      </span>
    </span>
  );
};
